import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import Decimal from 'decimal.js'
import { find } from 'lodash-es'
import { Stack } from '@mui/material'
import RipLineFormSceneMarker from '@sceneMarkers/FormSceneMarkers/RipLineFormSceneMarker'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { ErrorField, Form } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useResultsInvalidation } from '@editorHooks'
import { getModel, getVerticalTransmissionGraph } from '@queries'
import { createRips, deleteRip } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import { lineLikeSettingsOf } from '../../../PlanSettingsForm/utils'
import WallRipFormFields from '../../../WallRipForm/components/FormFields'
import { createRipSchema } from '../../../WallRipForm/components/WallRipFormBase/schema'

const ABS_POSITION_NUM_DECIMALS = 2

interface Props {
  positionGuid: string
  readOnly?: boolean
}

const RipGeometryForm = ({ positionGuid, readOnly = false }: Props): ReactElement | null => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const client = useQueryClient()
  const invalidateResults = useResultsInvalidation()

  const rips = useModelStore(state => state.model.rips)
  const walls = useModelStore(state => state.model.walls)

  const rip = useMemo(() => find(rips, { position_guid: positionGuid }), [rips, positionGuid])
  const wall = useMemo(() => {
    if (rip?.wall_guid) {
      return find(walls, { guid: rip.wall_guid })
    }
  }, [rip, walls]) as ShapeObject | undefined

  const wallLength = useMemo(() => wall?.shape.points[0].distanceTo(wall?.shape.points[1]), [wall])

  const { schema, defaultValues } = useMemo(() => {
    if (!rip || !wall || !wallLength) {
      return { schema: createRipSchema(positionGuid, ''), defaultValues: undefined }
    }

    const absolutePosition = wall.shape.points[0].distanceTo(rip.start)
    const absolutePositionRounded = absolutePosition.toFixed(ABS_POSITION_NUM_DECIMALS)
    const wallLengthRounded = wallLength.toFixed(ABS_POSITION_NUM_DECIMALS)
    const relativePosition = new Decimal(absolutePositionRounded).div(
      new Decimal(wallLengthRounded),
    )
    const relativePositionAsString = relativePosition.toString()

    const schema = createRipSchema(positionGuid, rip.wall_guid)

    const defaultValues = {
      ...schema.getDefault(),
      relative_position: relativePositionAsString,
      rip_start: { x: rip.start.x, y: rip.start.y, z: rip.start.z },
      rip_end: { x: rip.end.x, y: rip.end.y, z: rip.end.z },
    }

    return { schema, defaultValues }
  }, [rip, wall, wallLength, positionGuid])

  const { mutateAsync, isLoading } = useMutation(
    async (ripData: WallRipRequest) => {
      await deleteRip.request(projectId, ripData.guid)
      return await createRips.request(projectId, [ripData])
    },
    {
      onSuccess: async () => {
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Wandrippe erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Wandrippe'), {
          variant: 'error',
        })
      },
    },
  )

  if (!rip || !wall || !wallLength || !defaultValues) {
    return null
  }

  return (
    <Form
      key={`positionGuid-${rip.relative_position}`}
      // Re-creating the rip keeps its plan settings.
      onSubmit={data => mutateAsync({ ...data, ...lineLikeSettingsOf(rip) })}
      validationSchema={schema}
      defaultValues={defaultValues}
      validationContext={{ wall, rips }}
      data-cy="form-rip-geometry"
    >
      <Stack direction="column" spacing={1}>
        <RipLineFormSceneMarker />
        <WallRipFormFields guid={positionGuid} length={wallLength} readOnly={readOnly} />

        <ErrorField name="isIntersecting" />
        <ErrorField name="isOverlapping" />

        <SaveButton
          type="submit"
          fullWidth
          loading={isLoading}
          disabled={readOnly}
          data-cy="btn-save-rip-geometry"
          size="small"
        >
          Speichern
        </SaveButton>
      </Stack>
    </Form>
  )
}

export default RipGeometryForm
